import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [pending, setPending] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await fetch(`${API_BASE_URL}/api/admin/pending-members`, { headers }).then((r) => (r.ok ? r.json() : []));
      setPending(p);
    } catch { setError('Laden mislukt'); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const flash = (m: string) => { setToast(m); setError(''); setTimeout(() => setToast(''), 3500); };
  const act = async (id: string, path: string) => {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/members/${id}/${path}`, { method: 'POST', headers });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Mislukt'); }
      flash(path === 'approve' ? 'Aanmelding goedgekeurd — het lid kan nu inloggen.' : 'Aanmelding afgewezen.');
      load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Mislukt'); } finally { setBusy(''); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <p className="text-sm text-slate-500">
          Ondernemers melden zich zelf aan via het aanmeldproces (met SEPA-machtiging). Controleer de stamdata en keur de aanmelding goed of af.
        </p>
      </div>
      {toast && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">{toast}</div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-mfb-blue">Openstaande aanmeldingen</h2>
        <span className="text-sm text-slate-500">{pending.length} aanvraag(en)</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : pending.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          Geen openstaande aanmeldingen. Nieuwe aanvragen verschijnen hier automatisch zodra een ondernemer zich aanmeldt.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {pending.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold text-mfb-blue truncate">{m.company_name}</h3>
                  <p className="text-xs text-slate-400">Aangevraagd {m.aangevraagd_op} · {m.collectiviteit}</p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 shrink-0">Wacht op goedkeuring</span>
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Row k="Contactpersoon" v={m.full_name || '—'} />
                <Row k="E-mail" v={m.email} />
                <Row k="KvK" v={m.kvk || '—'} />
                <Row k="Telefoon" v={m.telefoon || '—'} />
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <span className="text-slate-400 text-xs uppercase tracking-wide">SEPA-machtiging</span>
                  {m.machtiging_actief ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">✓ Afgegeven {m.machtiging_iban ? `· ${m.machtiging_iban}` : ''}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">✗ Nog niet afgegeven</span>
                  )}
                </div>
              </dl>

              {!m.machtiging_actief && (
                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  Let op: zonder actieve machtiging kan het lid na goedkeuring niet verzekeren of kredietchecks aanvragen.
                </p>
              )}

              <div className="mt-4 flex gap-2">
                <button disabled={busy === m.id} onClick={() => act(m.id, 'approve')}
                  className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50">
                  Aanmelding goedkeuren
                </button>
                <button disabled={busy === m.id} onClick={() => { if (window.confirm('Aanmelding afwijzen?')) act(m.id, 'suspend'); }}
                  className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50">
                  Afwijzen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-slate-400 text-xs uppercase tracking-wide">{k}</dt>
      <dd className="text-slate-700 truncate">{v}</dd>
    </div>
  );
}

export default AdminUsersPage;
